import { motion } from "motion/react";
import { useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { trpc } from "~/api/trpc";
import { GTMEvent } from "~/components/gtmProvider/GTMContext";
import useGTM from "~/components/gtmProvider/useGTM";
import PrimaryActionButton from "~/components/new/PrimaryActionButton";
import TopProgressBar from "~/components/new/TopProgressBar";
import { SelectedPath, useBookingFlowStore } from "~/stores/booking-flow-store";
import { useCustomerAuthStore } from "~/stores/customer-auth-store";
import { Language, useLanguageStore } from "~/stores/language-store";
import { ArrowRight, LayoutGrid, Sparkles, LogOut } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";

type PathOption = {
    id: SelectedPath;
    titleDe: string;
    titleEn: string;
    descriptionDe: string;
    descriptionEn: string;
    icon: React.ReactNode;
};

type PathCardProps = {
    option: PathOption;
    isSelected: boolean;
    onSelect: () => void;
    language: Language;
};

const PathCard = ({ option, isSelected, onSelect, language }: PathCardProps) => {
    const title = language === Language.GERMAN ? option.titleDe : option.titleEn;
    const description = language === Language.GERMAN ? option.descriptionDe : option.descriptionEn;

    return (
        <div
            className={`flex flex-row items-start gap-4 p-4 rounded-[5px] bg-white border-2 transition-all duration-200 cursor-pointer drop-shadow-md h-full ${isSelected ? 'border-burgundy' : 'border-burgundy/20'}`}
            onClick={onSelect}
        >
            <div className="shrink-0 mt-0.5">
                {option.icon}
            </div>
            <div className="flex flex-col gap-1">
                <span className="font-semibold text-base text-burgundy">{title}</span>
                <span className="text-sm text-burgundy/80">{description}</span>
            </div>
        </div>
    );
};

// Fixed bottom bar with login prompt and continue button
type FixedBottomBarProps = {
    selectedPath: SelectedPath | null;
    continueButtonText: string;
    onContinue: () => void;
    onLoginClick: () => void;
    onLogout: () => void;
};

const FixedBottomBar = ({ selectedPath, continueButtonText, onContinue, onLoginClick, onLogout }: FixedBottomBarProps) => {
    const { t } = useLanguageStore();
    const { customer, isAuthenticated } = useCustomerAuthStore();

    return (
        <div className="fixed z-10 bottom-0 left-0 w-full md:max-w-7xl md:left-1/2 md:-translate-x-1/2 flex flex-col gap-3 px-6 pt-4 pb-6 bg-light-burgundy">
            {/* Login prompt or logged-in state */}
            {isAuthenticated() && customer ? (
                <div className='flex items-center justify-center gap-2 text-xs text-burgundy font-light'>
                    <span>{t('loggedInAs')} {customer.firstName}</span>
                    <button
                        onClick={onLogout}
                        className='flex items-center gap-1 text-burgundy/70 hover:text-burgundy transition-colors underline'
                    >
                        <LogOut className='w-3 h-3' />
                        {t('logout')}
                    </button>
                </div>
            ) : (
                <p className='text-xs text-center text-burgundy font-light'>
                    {t('loginPromptText')}{' '}
                    <button
                        onClick={onLoginClick}
                        className='text-burgundy underline hover:text-burgundy/70 transition-colors'
                    >
                        {t('loginPromptLink')}
                    </button>
                </p>
            )}
            <PrimaryActionButton
                onClick={onContinue}
                label={continueButtonText}
                disabled={!selectedPath}
                rightIcon={<ArrowRight />}
            />
        </div>
    );
};

const PathSelection = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { selectedPath, setSelectedPath } = useBookingFlowStore(
        useShallow((state) => ({
            selectedPath: state.selectedPath,
            setSelectedPath: state.setSelectedPath,
        }))
    );
    const { t, language } = useLanguageStore();
    const { customer, isAuthenticated, logout, startLogin } = useCustomerAuthStore();
    const { sendEvent } = useGTM();

    const handleLoginClick = () => {
        // Save current path so we can return after login
        startLogin(location.pathname + location.search);
        navigate("/login");
    };

    const handleLogout = () => {
        logout();
    };

    // Fetch CMS config
    const { data: cmsData } = trpc.publicBookingFunnel.getPathSelectionConfig.useQuery(undefined, {
        staleTime: 60000, // 1 minute cache
    });
    const config = cmsData?.config;
    const doctorImages = config?.doctorImages || [];

    // Build path options from CMS config
    const pathOptions: PathOption[] = useMemo(() => [
        {
            id: 'browse-by-symptom' as SelectedPath,
            titleDe: config?.path1TitleDe || 'Nach Kategorie durchsuchen',
            titleEn: config?.path1TitleEn || "Browse by Category",
            descriptionDe: config?.path1DescriptionDe || 'Entdecke unsere Behandlungen nach Kategorie sortiert.',
            descriptionEn: config?.path1DescriptionEn || 'Discover our treatments sorted by category.',
            icon: <LayoutGrid className="w-6 h-6 text-burgundy" />,
        },
        {
            id: 'browse-by-category' as SelectedPath,
            titleDe: config?.path2TitleDe || 'Nach Symptom durchsuchen',
            titleEn: config?.path2TitleEn || 'Browse by Symptom',
            descriptionDe: config?.path2DescriptionDe || 'Finde die richtige Behandlung basierend auf deinen Anliegen.',
            descriptionEn: config?.path2DescriptionEn || 'Find the right treatment based on your concerns.',
            icon: <Sparkles className="w-6 h-6 text-burgundy" />,
        },
    ], [config]);

    // Get localized text from CMS config
    const pageTitle = language === Language.GERMAN 
        ? (config?.pageTitleDe || 'Wähle, was am besten zu dir passt') 
        : (config?.pageTitleEn || 'Select what suits you best');
    const progressDisplay = language === Language.GERMAN 
        ? (config?.progressDisplayDe || 'SCHRITT 2 / 5') 
        : (config?.progressDisplayEn || 'STEP 2 / 5');
    const doctorSectionTitle = language === Language.GERMAN 
        ? (config?.doctorSectionTitleDe || '30+ Ärzt:innen und Medical Skin Experts') 
        : (config?.doctorSectionTitleEn || '30+ Doctors and Medical Skin Experts');
    const doctorSectionText = language === Language.GERMAN 
        ? (config?.doctorSectionTextDe || '') 
        : (config?.doctorSectionTextEn || '');
    const loginPromptText = language === Language.GERMAN 
        ? (config?.loginPromptTextDe || 'Du warst schon mal bei uns?') 
        : (config?.loginPromptTextEn || 'Have you been here before?');
    const loginPromptLinkText = language === Language.GERMAN 
        ? (config?.loginPromptLinkTextDe || 'Klicke hier um dich einzuloggen.') 
        : (config?.loginPromptLinkTextEn || 'Click here to login.');
    const continueButtonText = language === Language.GERMAN 
        ? (config?.continueButtonTextDe || 'WEITER') 
        : (config?.continueButtonTextEn || 'CONTINUE');

    useEffect(() => {
        window.scroll(0, 0);
        sendEvent(GTMEvent.BOOKING_FUNNEL_PATH_SELECTION, {});
    }, []);

    const handlePathSelect = (pathId: SelectedPath) => {
        if (selectedPath === pathId) {
            setSelectedPath(null);
        } else {
            setSelectedPath(pathId);
        }
    };

    const handleContinue = () => {
        if (!selectedPath) {
            return;
        }
        sendEvent(GTMEvent.BOOKING_FUNNEL_PATH_SELECTED, {
            selected_path: selectedPath,
        });
        navigate("/services");
    };

    const handleBack = () => {
        navigate("/");
    };

    // Fallback placeholder images if no doctor images are configured
    const displayImages = doctorImages.length > 0 
        ? doctorImages 
        : [
            { id: '1', url: '', altText: 'Doctor 1' },
            { id: '2', url: '', altText: 'Doctor 2' },
            { id: '3', url: '', altText: 'Doctor 3' },
            { id: '4', url: '', altText: 'Doctor 4' },
            { id: '5', url: '', altText: 'Doctor 5' },
        ];

    return (
        <motion.div
            className="flex flex-col min-h-screen"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
        >
            <TopProgressBar percentageFilled={40} displayString={progressDisplay} onClickBackButton={handleBack} />

            {/* Title */}
            <div className="flex flex-col items-center gap-4 px-6 py-6">
                <h1 className="text-2xl text-burgundy text-center">
                    {pageTitle}
                </h1>
            </div>

            {/* Path options */}
            <div className="flex-1 overflow-y-auto px-6 pb-16">
                <div className="flex flex-col md:flex-row gap-3">
                    {pathOptions.map((option, index) => (
                        <motion.div
                            key={option.id}
                            className="md:flex-1"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: 0.1 + index * 0.1, ease: "easeOut" }}
                        >
                            <PathCard
                                option={option}
                                isSelected={selectedPath === option.id}
                                onSelect={() => handlePathSelect(option.id)}
                                language={language}
                            />
                        </motion.div>
                    ))}
                </div>

                {/* Doctor avatars section */}
                <div className='flex flex-col gap-1 mt-8 px-6'>
                    <div className="mx-auto py-2 *:data-[slot=avatar]:ring-light-burgundy flex -space-x-2 *:data-[slot=avatar]:ring-2">
                        {displayImages.slice(0, 5).map((image, index) => (
                            <Avatar key={image.id} className='size-12'>
                                <AvatarImage src={image.url} alt={image.altText || `Doctor ${index + 1}`} />
                                <AvatarFallback>{(image.altText || 'DR')[0]}{(image.altText || 'DR')[1]}</AvatarFallback>
                            </Avatar>
                        ))}
                    </div>
                    <p className="text-xs font-semibold text-center text-burgundy">
                        {doctorSectionTitle}
                    </p>
                    {doctorSectionText && (
                        <p className="text-xs text-center text-burgundy">
                            {doctorSectionText}
                        </p>
                    )}
                </div>
            </div>

            <FixedBottomBar
                selectedPath={selectedPath}
                continueButtonText={continueButtonText}
                onContinue={handleContinue}
                onLoginClick={handleLoginClick}
                onLogout={handleLogout}
            />
        </motion.div>
    );
};

export default PathSelection;
